import { useState } from 'react';

import {
  Modal,
  ModalDialog,
  Typography,
  FormControl,
  FormLabel,
  Textarea,
  Input,
  Button,
  Stack,
  ModalOverflow,
  FormHelperText,
} from '@mui/joy';

import { track } from '../../../../services/analytics';
import {
  EVENT_DEVELOPER_PORTAL_PAGE,
  EVNT_developer_contact_us_submitted,
} from '../../../../services/analytics/events';
import { makeAuthenticatedPostRequest } from '../../../../services/axios';
import { contact } from '../../../../services/axios/endpoints';
import { useGlobalStore } from '../../../../stores/useGlobalStore';
import type { UpgradeSource } from '../../../../utils/constants/upgradeSources';

const ContactUsForm = ({
  open,
  onClose,
  source,
  defaultEmail,
}: {
  open: boolean;
  onClose: () => void;
  source: UpgradeSource;
  defaultEmail?: string;
}) => {
  const setSnackbar = useGlobalStore((s) => s.setSnackbar);
  const userInfo = useGlobalStore((s) => s.user);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: defaultEmail || userInfo?.email || '',
    message: '',
  });

  const [errors, setErrors] = useState({
    email: '',
    message: '',
  });

  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = () => {
    const errors = {
      email: formData.email === '' ? 'Please enter your email address.' : '',
      message: formData.message === '' ? 'Please enter your message.' : '',
    };
    setErrors(errors);
    return Object.values(errors).every((error) => error === '');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    track(EVNT_developer_contact_us_submitted, EVENT_DEVELOPER_PORTAL_PAGE);
    try {
      await makeAuthenticatedPostRequest(contact, {
        ...formData,
        source,
      });
      setIsSuccess(true);
    } catch (error) {
      setSnackbar({ message: 'Something went wrong. Please try again.', color: 'danger' });
    } finally {
      setIsLoading(false);
    }
  };

  const clearForm = () => {
    setFormData({
      email: userInfo?.email || '',
      message: '',
    });
    setErrors({
      email: '',
      message: '',
    });
    setIsSuccess(false);
  };

  const handleClose = () => {
    clearForm();
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalOverflow>
        <ModalDialog sx={{ width: 900, maxWidth: '90%' }} layout='center'>
          {isSuccess ? (
            <Stack gap={2}>
              <Typography level='body-lg'>
                Thank you very much for your message. We do our best to respond to every inquiry in
                a timely manner.
              </Typography>
              <Button
                variant='solid'
                onClick={handleClose}
                sx={{ width: 'fit-content', marginLeft: 'auto' }}
              >
                Close
              </Button>
            </Stack>
          ) : (
            <>
              <Typography level='h4' component='h2' id='contact-us-form' gutterBottom>
                Contact us
              </Typography>

              <Stack spacing={2}>
                <FormControl
                  required
                  error={!!errors.email}
                  sx={{ color: (theme) => theme.palette.primary.outlinedDisabledColor }}
                >
                  <FormLabel>Email:</FormLabel>
                  <Input
                    placeholder='Jane.doe@test.com'
                    type='email'
                    value={formData.email}
                    disabled
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                  <Typography
                    fontSize={'sm'}
                    sx={{ color: (theme) => theme.palette.text.tertiary }}
                  >
                    Use your professional email address.
                  </Typography>
                  {errors.email && <FormHelperText>{errors.email}</FormHelperText>}
                </FormControl>

                <FormControl required error={!!errors.message}>
                  <FormLabel>Message:</FormLabel>
                  <Textarea
                    placeholder='How can we help you?'
                    minRows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  />
                  {errors.message && <FormHelperText>{errors.message}</FormHelperText>}
                </FormControl>

                <Stack direction='row' spacing={2} justifyContent='flex-end' sx={{ mt: 2 }}>
                  <Button variant='plain' color='neutral' onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    variant='solid'
                    onClick={handleSubmit}
                    disabled={isLoading}
                    loading={isLoading}
                  >
                    Send
                  </Button>
                </Stack>
              </Stack>
            </>
          )}
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
};

export { ContactUsForm };
